/**
 * @file consent-texts-check.ts
 * @description Поля редакции текстов согласия платформы и их проверка при
 * публикации (phase19.16, партия Р3г, v0.19.62; решение пользователя
 * 2026-10-23 — тексты правит платформенный администратор; проект данных
 * docs/phases/phase19.16-materials/db-design-r3g-texts.md §3, §5, §6;
 * экран uiux-r3g-consent-texts.md §3, §5).
 *
 * Зачем в shared: одна проверка для формы кабинета платформы (ошибка у поля
 * до отправки) и API (`POST admin/platform/consent-texts/:kind` → 422
 * `consent_text_invalid` с `details`). Реестр полей `CONSENT_TEXT_FIELDS`
 * задаёт каждому полю подпись, предел длины, разрешённые вставки, запрет
 * точки в конце и виды баннера, в которых поле видно посетителю: проверка,
 * «Отличия от ред. N» и «какие баннеры затронуты» читают один реестр (OCP —
 * новое поле = строка реестра).
 *
 * Пределы — по db-design-r3g-texts §3 (решение оркестратора 2026-10-23), а
 * не по более строгим цифрам макета uiux §5.
 *
 * Хэш `contentSha256` считает API (node:crypto) от
 * `consentTextsCanonicalJson(texts)`: пакет работает и в браузере, поэтому
 * здесь только канонический JSON (как `sitePageCanonicalJson`).
 */

import { z } from "zod";

import { consentTextHasMarkup, SITE_CONSENT_CATEGORIES, type SiteConsentCategory } from "../site-theme/counters.js";
import { SITE_LIMITS } from "../site-theme/limits.js";
import { canonicalJson } from "../site-theme/snapshot.js";
import type {
  ConsentBannerTexts,
  ConsentNoticeRiskTexts,
  ConsentTextKind,
  ConsentTextsByKind,
  SiteConsentBannerKind,
} from "./consent-texts.js";

// ── Реестр полей ──

/** Вставки, которые подставляет код. */
export const CONSENT_TEXT_PLACEHOLDERS = ["categories", "services"] as const;
export type ConsentTextPlaceholder = (typeof CONSENT_TEXT_PLACEHOLDERS)[number];

/** Сколько раз вставка должна встретиться: [не меньше, не больше]. */
export type ConsentPlaceholderRule = Readonly<Partial<Record<ConsentTextPlaceholder, readonly [number, number]>>>;

export interface ConsentTextFieldInfo {
  readonly kind: ConsentTextKind;
  /** Путь в JSON редакции: `buttons.acceptAll`, `categories.ads.label`. */
  readonly path: string;
  /** Подпись для людей: ошибки, «Отличия от ред. N», окно публикации. */
  readonly label: string;
  /** Предел длины, кодовые точки. */
  readonly max: number;
  /** Разрешённые вставки; пусто — вставок в поле нет. */
  readonly placeholders: ConsentPlaceholderRule;
  /** Без знака конца предложения в конце: точку или запятую ставит код. */
  readonly noTrailingPunctuation: boolean;
  /** В каких видах баннера поле видит посетитель (у окна о риске — ни в каких). */
  readonly visibleIn: readonly SiteConsentBannerKind[];
}

const BOTH: readonly SiteConsentBannerKind[] = ["consent", "notice"];
const PHRASE: ConsentPlaceholderRule = { categories: [1, 1], services: [1, 1] };
const COMPACT: ConsentPlaceholderRule = { categories: [1, 1], services: [0, 1] };

const field = (
  kind: ConsentTextKind,
  path: string,
  label: string,
  max: number,
  visibleIn: readonly SiteConsentBannerKind[],
  extra: { placeholders?: ConsentPlaceholderRule; noTrailingPunctuation?: boolean } = {},
): ConsentTextFieldInfo => ({
  kind,
  path,
  label,
  max,
  placeholders: extra.placeholders ?? {},
  noTrailingPunctuation: extra.noTrailingPunctuation ?? false,
  visibleIn,
});

const CATEGORY_LABEL: Readonly<Record<SiteConsentCategory, string>> = { analytics: "Аналитика", ads: "Реклама" };

/** Все поля в порядке формы кабинета (uiux-r3g-consent-texts §3). */
export const CONSENT_TEXT_FIELDS: readonly ConsentTextFieldInfo[] = Object.freeze([
  // Вкладка «Баннер».
  field("BANNER", "introDefault", "Вступление по умолчанию", SITE_LIMITS.consentIntro, BOTH, { noTrailingPunctuation: true }),
  field("BANNER", "consentPhrase", "Фраза «Только после согласия»", 300, ["consent"], {
    placeholders: PHRASE,
    noTrailingPunctuation: true,
  }),
  field("BANNER", "noticePhrase", "Фраза «Уведомление»", 300, ["notice"], { placeholders: PHRASE, noTrailingPunctuation: true }),
  field("BANNER", "compactConsent", "Компактная полоса «после согласия»", 160, ["consent"], { placeholders: COMPACT }),
  field("BANNER", "compactNotice", "Компактная полоса «Уведомление»", 160, ["notice"], { placeholders: COMPACT }),
  field("BANNER", "info", "Без счётчиков", 200, ["info"]),
  field("BANNER", "more", "Ссылка на политику ПДн", 40, ["consent", "notice", "info"]),
  // Вкладка «Окно «Настроить»».
  // У `info` заголовок — подпись области баннера для скринридера (aria-label).
  field("BANNER", "settingsTitle", "Заголовок окна «Настроить»", 60, ["consent", "notice", "info"]),
  field("BANNER", "alwaysOn", "Подпись «Всегда включены»", 40, BOTH),
  field("BANNER", "necessary.label", "Необходимые: название", 40, BOTH),
  field("BANNER", "necessary.description", "Необходимые: описание", 300, BOTH),
  ...SITE_CONSENT_CATEGORIES.flatMap((id) => [
    field("BANNER", `categories.${id}.label`, `${CATEGORY_LABEL[id]}: название`, 40, BOTH),
    field("BANNER", `categories.${id}.description`, `${CATEGORY_LABEL[id]}: описание`, 300, BOTH),
    ...(id === "analytics"
      ? [field("BANNER", "analyticsWebvisorDescription", "Аналитика: описание, когда включён Вебвизор", 300, BOTH)]
      : []),
    field("BANNER", `categoryGenitive.${id}`, `${CATEGORY_LABEL[id]}: во фразе`, 40, BOTH),
  ]),
  // Вкладка «Кнопки и подвал».
  // «Принять все» есть и в окне «Настроить» у режима «Уведомление».
  field("BANNER", "buttons.acceptAll", "Кнопка «принять все»", 32, BOTH),
  field("BANNER", "buttons.necessaryOnly", "Кнопка «только необходимые»", 32, ["consent"]),
  field("BANNER", "buttons.configure", "Кнопка «настроить»", 32, BOTH),
  field("BANNER", "buttons.ok", "Кнопка «понятно»", 32, ["notice", "info"]),
  field("BANNER", "buttons.refuse", "Кнопка «отказаться»", 32, ["notice"]),
  field("BANNER", "buttons.save", "Кнопка «сохранить выбор»", 32, BOTH),
  field("BANNER", "settingsLink", "Ссылка в подвале", 40, BOTH),
  // Вкладка «Окно о риске» — видит только владелец магазина.
  field("NOTICE_RISK", "title", "Окно о риске: заголовок", 80, []),
  field("NOTICE_RISK", "body", "Окно о риске: текст", 800, []),
  field("NOTICE_RISK", "acknowledge", "Окно о риске: подпись галочки", 120, []),
]);

/** Поля одного вида редакции. */
export function consentTextFields(kind: ConsentTextKind): ConsentTextFieldInfo[] {
  return CONSENT_TEXT_FIELDS.filter((f) => f.kind === kind);
}

/** Пределы длины полей вида — для подсказок формы (`GET …/current` отдаёт их как `limits`). */
export function consentTextLimits(kind: ConsentTextKind): Record<string, number> {
  return Object.fromEntries(consentTextFields(kind).map((f) => [f.path, f.max]));
}

// ── Схемы формы ──

const str = z.string();
const categoryTexts = z.strictObject({ label: str, description: str });
const perCategory = <T extends z.ZodType>(schema: T) =>
  z.strictObject({ analytics: schema, ads: schema } satisfies Record<SiteConsentCategory, T>);

/** Форма редакции баннера: все ключи, без лишних (содержание проверяет `validateConsentTexts`). */
export const consentBannerTextsSchema = z.strictObject({
  introDefault: str,
  consentPhrase: str,
  noticePhrase: str,
  compactConsent: str,
  compactNotice: str,
  info: str,
  more: str,
  categoryGenitive: perCategory(str),
  necessary: categoryTexts,
  categories: perCategory(categoryTexts),
  analyticsWebvisorDescription: str,
  buttons: z.strictObject({ acceptAll: str, necessaryOnly: str, configure: str, ok: str, refuse: str, save: str }),
  settingsTitle: str,
  alwaysOn: str,
  settingsLink: str,
});

export const consentNoticeRiskTextsSchema = z.strictObject({ title: str, body: str, acknowledge: str });

// Схема и интерфейс не должны разойтись: лишнее или недостающее поле — ошибка компиляции.
type SameShape<A, B> = [A] extends [B] ? ([B] extends [A] ? true : never) : never;
const _bannerShape: SameShape<z.infer<typeof consentBannerTextsSchema>, ConsentBannerTexts> = true;
const _riskShape: SameShape<z.infer<typeof consentNoticeRiskTextsSchema>, ConsentNoticeRiskTexts> = true;
void _bannerShape;
void _riskShape;

const SCHEMAS: { readonly [K in ConsentTextKind]: z.ZodType<ConsentTextsByKind[K]> } = {
  BANNER: consentBannerTextsSchema,
  NOTICE_RISK: consentNoticeRiskTextsSchema,
};

// ── Проверка ──

/** Код ответа API для ошибок проверки (`details` — список `ConsentTextIssue`). */
export const CONSENT_TEXT_INVALID = "consent_text_invalid";
/** Код ответа API, когда тексты совпадают с текущей редакцией (sha тот же). */
export const CONSENT_TEXT_UNCHANGED = "consent_text_unchanged";

/**
 * Причина ошибки поля (db-design-r3g-texts §3). Сверх списка проекта:
 * `invalid_shape` — нет ключа, лишний ключ или не строка; `whitespace` —
 * пробелы по краям или перевод строки («без пробелов по краям и переводов
 * строки» из того же §3).
 */
export type ConsentTextIssueCode =
  | "invalid_shape"
  | "empty"
  | "whitespace"
  | "too_long"
  | "markup"
  | "placeholder_missing"
  | "placeholder_duplicate"
  | "placeholder_forbidden"
  | "trailing_punctuation"
  | "same_as";

export interface ConsentTextIssue {
  /** Путь поля; пусто — вся редакция. */
  field: string;
  code: ConsentTextIssueCode;
  message: string;
}

export type ConsentTextsValidation = { ok: true } | { ok: false; errors: ConsentTextIssue[] };

/** Любая `{…}` в тексте — кандидат во вставку. */
const PLACEHOLDER = /\{([^{}]*)\}/gu;
const TRAILING_PUNCTUATION = /[.!?…]$/u;
const codePoints = (text: string): number => [...text].length;

const isPlaceholder = (name: string): name is ConsentTextPlaceholder =>
  (CONSENT_TEXT_PLACEHOLDERS as readonly string[]).includes(name);

/** Значение по пути `a.b.c`. */
function valueAt(root: unknown, path: string): unknown {
  let node: unknown = root;
  for (const key of path.split(".")) {
    if (typeof node !== "object" || node === null) return undefined;
    node = (node as Record<string, unknown>)[key];
  }
  return node;
}

function placeholderIssues(info: ConsentTextFieldInfo, text: string): ConsentTextIssue[] {
  const out: ConsentTextIssue[] = [];
  const counts: Record<ConsentTextPlaceholder, number> = { categories: 0, services: 0 };
  for (const match of text.matchAll(PLACEHOLDER)) {
    const name = match[1] ?? "";
    if (!isPlaceholder(name)) {
      out.push({
        field: info.path,
        code: "placeholder_forbidden",
        message: `Вставки {${name}} нет. Доступны {categories} и {services}.`,
      });
      continue;
    }
    if (info.placeholders[name] === undefined) {
      out.push({
        field: info.path,
        code: "placeholder_forbidden",
        message: "Здесь вставки не работают — текст покажется как есть.",
      });
      continue;
    }
    counts[name] += 1;
  }
  for (const name of CONSENT_TEXT_PLACEHOLDERS) {
    const rule = info.placeholders[name];
    if (rule === undefined) continue;
    const [min, max] = rule;
    if (counts[name] < min) {
      out.push({
        field: info.path,
        code: "placeholder_missing",
        message:
          name === "services"
            ? "Во фразе должна быть вставка {services} — без неё посетитель не узнает, какие сервисы работают."
            : `Во фразе должна быть вставка {${name}} — один раз.`,
      });
    } else if (counts[name] > max) {
      out.push({ field: info.path, code: "placeholder_duplicate", message: `Вставка {${name}} — не больше одного раза.` });
    }
  }
  return out;
}

/** Ошибки одного поля-строки. */
function fieldIssues(info: ConsentTextFieldInfo, text: string): ConsentTextIssue[] {
  if (text.trim() === "") {
    return [{ field: info.path, code: "empty", message: "Заполните — без этого текста баннер не покажется." }];
  }
  const out: ConsentTextIssue[] = [];
  if (text !== text.trim() || /[\r\n]/u.test(text)) {
    out.push({ field: info.path, code: "whitespace", message: "Без пробелов по краям и переводов строки." });
  }
  if (codePoints(text) > info.max) {
    out.push({ field: info.path, code: "too_long", message: `Не длиннее ${info.max} знаков.` });
  }
  if (consentTextHasMarkup(text)) {
    out.push({ field: info.path, code: "markup", message: "Простой текст: без ссылок и угловых скобок." });
  }
  out.push(...placeholderIssues(info, text));
  if (info.noTrailingPunctuation && TRAILING_PUNCTUATION.test(text.trim())) {
    out.push({
      field: info.path,
      code: "trailing_punctuation",
      message: "Точку в конце ставит платформа — уберите, иначе будет две.",
    });
  }
  return out;
}

/**
 * Проверка редакции перед публикацией (db-design-r3g-texts §3): строгая
 * форма (все ключи, категории — `SITE_CONSENT_CATEGORIES`), затем каждое
 * поле по реестру. Описание «Аналитики» с Вебвизором обязано отличаться от
 * обычного: иначе посетитель не узнает о записи действий (SB133).
 */
export function validateConsentTexts(kind: ConsentTextKind, texts: unknown): ConsentTextsValidation {
  const shape = SCHEMAS[kind].safeParse(texts);
  if (!shape.success) {
    return {
      ok: false,
      errors: shape.error.issues.map((issue) => ({
        field: issue.path.map(String).join("."),
        code: "invalid_shape" as const,
        message: "Неполная или лишняя структура текстов: нужны все поля редакции и только они.",
      })),
    };
  }
  const errors: ConsentTextIssue[] = [];
  for (const info of consentTextFields(kind)) {
    errors.push(...fieldIssues(info, valueAt(shape.data, info.path) as string));
  }
  if (kind === "BANNER") {
    const banner = shape.data as ConsentBannerTexts;
    if (banner.analyticsWebvisorDescription.trim() === banner.categories.analytics.description.trim()) {
      errors.push({
        field: "analyticsWebvisorDescription",
        code: "same_as",
        message: "Описание с Вебвизором должно отличаться от обычного: в нём сказано о записи действий.",
      });
    }
  }
  return errors.length === 0 ? { ok: true } : { ok: false, errors };
}

/**
 * Канонический JSON текстов (ключи по алфавиту на любой глубине) — вход
 * `sha256` для `SiteConsentTextRevision.contentSha256` и сравнения «тексты
 * не изменились» (`consent_text_unchanged`).
 */
export function consentTextsCanonicalJson(texts: ConsentBannerTexts | ConsentNoticeRiskTexts): string {
  return canonicalJson(texts);
}
